import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Save, AlertTriangle, RotateCcw, Hash } from "lucide-react";

function Field({ label, k, mono, c, setC }) {
    return (
        <div>
            <label className="hdb-label">{label}</label>
            <input className={`hdb-input ${mono ? "hdb-mono" : ""}`} value={c[k] || ""}
                   onChange={(e) => setC({ ...c, [k]: e.target.value })}
                   data-testid={`settings-${k}`} />
        </div>
    );
}

export default function Settings() {
    const [c, setC] = useState(null);
    const [nextInfo, setNextInfo] = useState(null);
    const [nextInput, setNextInput] = useState("");

    const loadCounter = () => api.get("/admin/next-invoice-number").then((r) => setNextInfo(r.data));
    useEffect(() => {
        api.get("/company").then((r) => setC(r.data));
        loadCounter();
    }, []);

    if (!c) return <div>Loading…</div>;

    const save = async () => {
        try { await api.put("/company", c); toast.success("Company details saved"); }
        catch { toast.error("Save failed"); }
    };
    const F = (label, k, mono = false) => <Field label={label} k={k} mono={mono} c={c} setC={setC} />;

    const applyNextNumber = async () => {
        const n = parseInt(nextInput, 10);
        if (!n || n < 1) return toast.error("Enter a positive number (e.g. 47)");
        if (!window.confirm(`Set the next invoice number to ${String(n).padStart(4, "0")}? This does NOT change existing invoices.`)) return;
        try {
            const r = await api.post("/admin/set-next-invoice-number", { next_number: n });
            toast.success(`Next invoice will be ${r.data.next_invoice_will_be}`);
            setNextInput("");
            loadCounter();
        } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
    };

    const resetInvoices = async () => {
        const s = window.prompt('This DELETES every invoice and resets the numbering.\nType DELETE (all caps) to confirm:');
        if (s !== "DELETE") return;
        try {
            const r = await api.post("/admin/reset-invoices", null, { params: { confirm: "DELETE" } });
            toast.success(`${r.data.invoices_deleted} invoices deleted`);
            loadCounter();
        } catch (e) { toast.error(e.response?.data?.detail || "Reset failed"); }
    };

    const resetItems = async () => {
        const s = window.prompt('This DELETES every item in your Item Master.\nType DELETE (all caps) to confirm:');
        if (s !== "DELETE") return;
        try {
            const r = await api.post("/admin/reset-items", null, { params: { confirm: "DELETE" } });
            toast.success(`${r.data.items_deleted} items deleted`);
        } catch (e) { toast.error(e.response?.data?.detail || "Reset failed"); }
    };

    return (
        <div data-testid="settings-page" className="max-w-3xl">
            <h1 className="text-3xl hdb-heading font-black text-slate-900 mb-2">Company Settings</h1>
            <p className="text-slate-500 mb-6 text-sm">These details appear on every invoice</p>

            <div className="hdb-card p-6 space-y-4 mb-6">
                {F("Company Name", "name")}
                {F("Address", "address")}
                <div className="grid grid-cols-2 gap-4">
                    {F("GSTIN", "gstin", true)}
                    {F("Invoice Prefix", "invoice_prefix", true)}
                </div>
                <div className="grid grid-cols-2 gap-4">
                    {F("State", "state")}
                    {F("State Code", "state_code", true)}
                </div>
                {F("Contact", "contact")}
                <div className="pt-2">
                    <button className="hdb-btn hdb-btn-primary" onClick={save} data-testid="save-settings">
                        <Save size={14} /> Save Changes
                    </button>
                </div>
            </div>

            {/* Invoice numbering */}
            <div className="hdb-card p-6 mb-6">
                <div className="flex items-center gap-2 mb-2">
                    <Hash size={18} className="text-slate-500" />
                    <h2 className="hdb-heading font-bold text-lg text-slate-900">Invoice numbering</h2>
                </div>
                <p className="text-xs text-slate-500 mb-4">
                    Set the starting invoice number when migrating from an existing billing system.
                    Numbers auto-increment from here for the current financial year.
                </p>
                {nextInfo && (
                    <div className="bg-slate-50 border border-slate-200 rounded-md p-4 mb-4">
                        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Next invoice will be</div>
                        <div className="hdb-mono text-xl font-black text-slate-900 mt-1" data-testid="next-invoice-preview">
                            {nextInfo.preview}
                        </div>
                        <div className="text-xs text-slate-500 mt-1">FY {nextInfo.fy} · prefix {nextInfo.prefix}</div>
                    </div>
                )}
                <div className="flex gap-3 items-end">
                    <div className="flex-1 max-w-xs">
                        <label className="hdb-label">Set next number to</label>
                        <input className="hdb-input hdb-mono" type="number" min="1" placeholder="e.g. 47"
                               value={nextInput} onChange={(e) => setNextInput(e.target.value)}
                               data-testid="next-number-input" />
                    </div>
                    <button className="hdb-btn hdb-btn-primary" onClick={applyNextNumber}
                            data-testid="apply-next-number">
                        Apply
                    </button>
                </div>
                <div className="text-[11px] text-slate-500 mt-3">
                    Example: If your existing billing system last issued <span className="hdb-mono">HBEPL-046</span>,
                    enter <span className="hdb-mono">47</span> and HDB will start from <span className="hdb-mono">HBEPL/{nextInfo?.fy || "YY-YY"}/0047</span>.
                </div>
            </div>

            {/* Danger zone */}
            <div className="border border-red-200 rounded-md p-6 bg-red-50/40" data-testid="danger-zone">
                <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle size={18} className="text-red-600" />
                    <h2 className="hdb-heading font-bold text-lg text-red-900">Danger zone — one-time cleanup</h2>
                </div>
                <p className="text-xs text-red-800/80 mb-5">
                    Use these before going live to delete the sample / testing data. These actions are permanent.
                </p>

                <div className="flex justify-between items-center py-3 border-t border-red-200">
                    <div>
                        <div className="font-semibold text-slate-900 text-sm">Delete all invoices</div>
                        <div className="text-xs text-slate-600">Removes every invoice and resets the numbering counter. Items and company profile are kept.</div>
                    </div>
                    <button className="hdb-btn hdb-btn-danger" onClick={resetInvoices} data-testid="reset-invoices-btn">
                        <RotateCcw size={13} /> Delete invoices
                    </button>
                </div>

                <div className="flex justify-between items-center py-3 border-t border-red-200">
                    <div>
                        <div className="font-semibold text-slate-900 text-sm">Delete all items</div>
                        <div className="text-xs text-slate-600">Wipes the Item Master (e.g. before importing your real catalogue).</div>
                    </div>
                    <button className="hdb-btn hdb-btn-danger" onClick={resetItems} data-testid="reset-items-btn">
                        <RotateCcw size={13} /> Delete items
                    </button>
                </div>
            </div>
        </div>
    );
}
